/* eslint-disable no-underscore-dangle,no-await-in-loop */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useCallback, useState } from 'react'
import prepareHeaders from '../utils/prepareHeaders'
import { delay } from '../utils/utils'

export const HUB_TASK_STATUS = {
  SUCCESS: 'SUCCESS',
  FAIL: 'FAILED',
}

export const getHubErrorMessage = (data) => {
  const errorsJsonStr = data?._source?.errors

  let message = 'Failed to perform action'
  try {
    const errors = JSON.parse(errorsJsonStr)
    message =
      errors?.[0]?.innerError?.message || errors?.[0]?.message?.Message || errors?.[0]?.message || String(errorsJsonStr)
  } catch (e) {
    // ignore
  }

  return message
}

export const getHubTaskResponse = (data) => {
  const response = data?._source?.response
  if (response && typeof response === 'object') return response

  try {
    return response ? JSON.parse(response) : null
  } catch (e) {
    return null
  }
}

/** Resolves a user-facing message after queueing a hub task and polling to completion. */
export const resolvePollTaskError = (pollResult, { queueError, fallback = 'Failed to perform action' } = {}) => {
  if (queueError?.data?.message) {
    return queueError.data.message
  }

  const pollError = pollResult?.error
  if (typeof pollError === 'string') {
    return pollError
  }

  if (pollError?.message) {
    return pollError.message
  }

  if (pollResult?.data?._source) {
    return getHubErrorMessage(pollResult.data)
  }

  return fallback
}

export const hubApi = createApi({
  reducerPath: 'hub',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getTaskStatus: builder.query({
      query: (taskId) => ({
        url: `/hub/${taskId}/status`,
      }),
    }),
  }),
})

export const { useGetTaskStatusQuery, useLazyGetTaskStatusQuery } = hubApi

export const usePollTaskStatus = () => {
  const maxRetries = 180

  const [isFetching, setIsFetching] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState(null)
  const [getTaskStatus, result] = useLazyGetTaskStatusQuery()

  const pollTaskStatus = useCallback(
    async (taskId) => {
      setIsFetching(true)
      setIsSuccess(false)
      setIsError(false)
      setError(null)
      let status = null
      let pollResult = null
      let retries = 0

      try {
        do {
          await delay(5 * 1000)
          pollResult = await getTaskStatus(taskId)
          status = pollResult?.data?._source?.status
          retries += 1
        } while (
          !pollResult.isError &&
          status !== HUB_TASK_STATUS.SUCCESS &&
          status !== HUB_TASK_STATUS.FAIL &&
          retries < maxRetries
        )
      } catch (err) {
        // any error should be handled by rtk query internally, if there is any unexpected error just stop execution, log and return
        console.error(err)
      }

      const taskSuccess = status === HUB_TASK_STATUS.SUCCESS
      const taskFailed = status === HUB_TASK_STATUS.FAIL
      const timedOut = !taskSuccess && !taskFailed && !pollResult?.isError
      let resolvedError = pollResult?.error
      if (taskFailed) {
        resolvedError = { message: getHubErrorMessage(pollResult.data), data: pollResult.data }
      } else if (timedOut) {
        resolvedError = { message: 'Task timed out waiting for completion' }
      }
      setIsSuccess(taskSuccess)
      setIsError(!taskSuccess)
      setError(resolvedError)
      setIsFetching(false)
      return { ...pollResult, isSuccess: taskSuccess, isError: !taskSuccess, error: resolvedError }
    },
    [getTaskStatus],
  )

  return { ...result, isSuccess, isError, error, pollTaskStatus, isFetching }
}
